import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Spinner from 'srcReact/components/common2/Spinner'
import { TextBox } from 'srcReact/components/common2/TextBox'
import ContactHeader from '../ContactHeader'
import styles from './styles.module.scss'
import { isEmail, isEmpty } from 'srcReact/helpers/validator'
import Label from 'srcReact/components/common2/Label'
import IconButton from 'srcReact/components/common2/IconButton'
import { useDispatch, useSelector } from 'react-redux'
import { commonActions } from 'srcReact/components'
import RequiredStar from 'srcReact/components/common2/RequiredStar'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import Button from 'srcReact/components/common2/Button'
import { appConstantSettings } from 'srcReact/appConstantSettings'

function ContactForm({
  handleSubmit,
  isAuthorized,
  title,
  error,
  loadingMessage,
  isCloseMyAccount,
  authFields,
  nonAuthFields
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [validationError, setValidationError] = useState('')
  const [fileIds, setFileIds] = useState('')
  const [files, setFiles] = useState([])
  const fileInputRef = useRef(null)
  const [filterFields, setFilterFields] = useState(isAuthorized ? authFields : nonAuthFields)

  const {
    common: {
      fileCreation: { files: uploadedFiles, loadingFile }
    }
  } = useSelector((state) => state)

  const [contact, setContact] = useState({
    firstname: {
      value: '',
      placeholder: t('formlabel.firstname_placeholder'),
      hasError: false,
      errorMessage: '',
      isRequired: true
    },
    lastname: {
      value: '',
      placeholder: t('formlabel.lastname_placeholder'),
      hasError: false,
      errorMessage: '',
      isRequired: true
    },
    email: {
      value: '',
      placeholder: t('formlabel.email_placeholder'),
      hasError: false,
      errorMessage: '',
      isRequired: true
    },
    country: {
      value: '',
      placeholder: t('formlabel.country_placeholder'),
      hasError: false,
      errorMessage: '',
      isRequired: false
    },
    occupation: {
      value: '',
      placeholder: t('formlabel.occupation_placeholder'),
      hasError: false,
      errorMessage: '',
      isRequired: false
    },
    organization: {
      value: '',
      placeholder: t('formlabel.organization_placeholder'),
      hasError: false,
      errorMessage: '',
      isRequired: false
    },
    subject: {
      value: isCloseMyAccount ? t('account_settings.sidebar.deleteAcc') : '',
      hasError: false,
      errorMessage: '',
      isRequired: true
    },
    message: {
      value: isCloseMyAccount ? t('contact.close-my-account') : '',
      placeholder: t('contact.tell-us-about'),
      hasError: false,
      errorMessage: '',
      isRequired: true
    }
  })

  useEffect(() => {
    if (!loadingFile && uploadedFiles?.length > 0) {
      const fileIds = uploadedFiles?.map((file) => file.id).join(',')
      handleSubmit(contact, fileIds)
    }
  }, [uploadedFiles, loadingFile])

  function handleSendMessage(e) {
    e.preventDefault()

    setValidationError('')
    if (validate(contact)) {
      if (files?.length > 0) {
        dispatch(commonActions.fileCreation(files, { is_logged: isAuthorized ? 1 : 0, type: 'picture' }))
      } else {
        handleSubmit(contact)
      }
    } else {
      setValidationError(t('formlabel.validation_error'))
    }
  }

  function validate(contact) {
    let isValid = true
    const strContactToValidate = JSON.stringify(contact)
    const contactToValidate = JSON.parse(strContactToValidate)

    for (const key in contactToValidate) {
      if (!isFieldValid(contactToValidate, key)) {
        isValid = false
      }
    }
    setContact({ ...contactToValidate })
    return isValid
  }

  function isFieldValid(objContact, key) {
    if (filterFields.includes(key)) {
      if (objContact[key].isRequired && isEmpty(objContact[key].value)) {
        objContact[key] = {
          ...objContact[key],
          hasError: true,
          errorMessage: t('error.required', { field: t(`formlabel.${key}`) })
        }
        return false
      } else if (key === 'email' && !isEmail(objContact[key].value, true)) {
        objContact[key] = {
          ...objContact[key],
          hasError: true,
          errorMessage: t('error.invalid_email', { field: t(`formlabel.${key}`) })
        }
        return false
      }
    }

    return true
  }

  function handleChange(e) {
    if (e && e.target) {
      const { name, value } = e.target
      setContact({ ...contact, [name]: { ...contact[name], value: value, hasError: false } })
    }
  }

  function handleBlur(e) {
    if (e && e.target) {
      const { name, value } = e.target
      if (!isFieldValid(contact, name)) {
        setContact({ ...contact, [name]: { ...contact[name], value: value, hasError: true } })
      }
    }
  }
  async function onSelectFile(e) {
    const newFiles = e.target.files
    if (newFiles?.length > 0) {
      setFiles([...files, ...newFiles])
    }
  }

  function removeFile(fileIndex) {
    const updatedFiles = files.toSpliced(fileIndex, 1)
    setFiles(updatedFiles)
  }

  const openFileDialog = (e) => {
    e.stopPropagation()
    fileInputRef.current.click()
  }
  return (
    <React.Fragment>
      <ContactHeader title={title} />
      {(error || validationError) && <ErrorMessage text={error || validationError} />}
      <form className={styles.formWrapper}>
        {Object.keys(contact).map(function (key, index) {
          if (key === 'message') {
            return (
              <div className={`${styles.controlWrapper} ${styles.message}`} key={index}>
                <Label className={styles.label}>
                  {t('global.message')}
                  {contact[key].isRequired && <RequiredStar />}
                </Label>
                <textarea
                  name='message'
                  className={`${styles.textarea} ${contact[key].hasError ? styles.error : ''}`}
                  placeholder={isAuthorized ? t('contact.tell-us-about-auth') : contact[key].placeholder}
                  onChange={(e) => handleChange(e)}
                  onBlur={(e) => handleBlur(e)}
                  aria-label={contact[key].placeholder}
                  aria-required={contact[key].isRequired}
                  defaultValue={contact[key].value}
                  maxLength={appConstantSettings.MESSAGE_MAX_LEN}
                />
                {contact[key].hasError && <ErrorMessage text={contact[key].errorMessage} />}
              </div>
            )
          }

          return filterFields.includes(key) ? (
            <TextBox
              key={index}
              fieldInfo={contact[key]}
              label={`${t(`formlabel.${key}`)}`}
              name={key}
              customWrapperCss={`${styles.controlWrapper} ${styles[key]}`}
              customLabelCss={styles.label}
              // customErrorCss={styles.error}
              customInputCss={`${styles.input} ${contact[key].hasError ? styles.error : ''}`}
              isRequired={contact[key].isRequired}
              onChange={handleChange}
              onBlur={handleBlur}
              {...(key === 'subject' ? { maxLength: appConstantSettings.SUBJECT_MAX_LEN } : {})}
            />
          ) : null
        })}
        <div className={`${styles.controlWrapper} ${styles.attachement}`}>
          <Label className={styles.label}>{t('contact.attachmentsOpt')}</Label>
          <button type='button' className={styles.btnFile} onClick={openFileDialog}>
            <span>{t('global.select')}</span>
            <input
              ref={fileInputRef}
              className={styles.fileInput}
              accept='image/*'
              type='file'
              multiple
              onChange={onSelectFile}
            />
          </button>
          <div className={styles.attachedFiles}>
            {files.map((file, index) => (
              <div className={styles.file}>
                <span className={styles.filename}>{file.name}</span>
                <IconButton
                  type='button'
                  iconName='close24'
                  iconSize='large24'
                  className={styles.btnRemove}
                  onClick={() => removeFile(index)}
                />
              </div>
            ))}
          </div>
        </div>

        <Button
          size='large'
          text={t('contact.send-message')}
          handleClick={(e) => handleSendMessage(e)}
          customStyle={styles.submit}
        />
      </form>
      {loadingMessage || (loadingFile && <Spinner isWhite={true} customCss={styles.spinner} />)}
    </React.Fragment>
  )
}

export default ContactForm
